import type { Quiz } from '../services/api';

function isStopQuiz(quiz: Quiz): boolean {
  const f = (quiz.format || '').toString();
  return f === 'Stop' || f === 'stop';
}

function isGeniusQuiz(quiz: Quiz): boolean {
  const f = (quiz.format || '').toString();
  return f === 'Genius' || f === 'genius';
}

function isTileMatchQuiz(quiz: Quiz): boolean {
  const f = (quiz.format || '').toString();
  return f === 'TileMatch' || f === 'tilematch';
}

/** Solo/PvP: enabled play bands from quiz authoring flags (fallback single band from catalog difficulty). */
export function playModeChoices(quiz: Quiz): string[] {
  const out: string[] = [];
  if (quiz.supportsPlayDifficultyEasy) out.push('Easy');
  if (quiz.supportsPlayDifficultyMedium) out.push('Medium');
  if (quiz.supportsPlayDifficultyHard) out.push('Hard');
  if (out.length > 0) return out;
  const d = (quiz.difficulty || 'Easy').trim();
  if (d === 'Easy' || d === 'Medium' || d === 'Hard') return [d];
  return ['Easy'];
}

/** Career-gated bands until missions unlock — Easy stays available when offered. */
export function isPlayBandCareerLocked(quiz: Quiz, band: string): boolean {
  if (isStopQuiz(quiz) || isGeniusQuiz(quiz) || isTileMatchQuiz(quiz)) return false;
  if (band === 'Medium')
    return quiz.supportsPlayDifficultyMedium === true && quiz.playMediumUnlockedForUser === false;
  if (band === 'Hard')
    return quiz.supportsPlayDifficultyHard === true && quiz.playHardUnlockedForUser === false;
  return false;
}

/** Bands the user can pick for this quiz (solo/PvP lobby). */
export function unlockedPlayModeChoices(quiz: Quiz): string[] {
  return playModeChoices(quiz).filter((b) => !isPlayBandCareerLocked(quiz, b));
}

const PLAY_DIFFICULTY_RANK: Record<string, number> = {
  Easy: 0,
  Medium: 1,
  Hard: 2,
};

/** Pre-select the highest play band the user has unlocked (Hard > Medium > Easy). */
export function defaultHighestUnlockedPlayDifficulty(quiz: Quiz): string {
  const choices = playModeChoices(quiz);
  const unlocked = unlockedPlayModeChoices(quiz);
  if (unlocked.length > 0) {
    return unlocked.reduce((best, b) =>
      (PLAY_DIFFICULTY_RANK[b] ?? -1) > (PLAY_DIFFICULTY_RANK[best] ?? -1) ? b : best
    );
  }
  const raw = (quiz.difficulty || '').trim();
  if (raw && choices.includes(raw) && !isPlayBandCareerLocked(quiz, raw)) return raw;
  return choices[0] ?? 'Easy';
}
